// StudioFinishEditor — shared component (Jun 17 extraction).
//
// Originally inlined in /appv3/page.tsx as the wizard's "post-render
// finishing surface". Extracted here so /creatives/:id can render the
// SAME UX when editing a saved creative from My Creatives. The /appv3
// inline copy is intentionally kept untouched until this extraction
// proves out — once it does, /appv3 can swap to importing from here
// and delete its duplicate.
//
// Architecture: pure presentation. All state (overlays, edit prompt,
// save status, etc.) is owned by the parent; the editor renders the
// UI and fires callbacks. Drag/clamp/scale machinery is the canonical
// StepText implementation (memory: feedback_text_drag).

import { useEffect, useMemo, useRef, useState } from 'react';
import {
  OVERLAY_PAD_FRAC,
  REFERENCE_CANVAS_WIDTH,
  aspectClass,
  aspectToRefHeight,
  clampAnchor,
  getTextLayerStyle,
  resolveStackedLayers,
  snapForPosition,
  suggestTextColorsFromImage,
  type LogoOverlay,
  type LogoTint,
  type TextOverlay,
} from '@/pages/app/lib/composeRender';
import { type Quality } from '@/lib/api';
import MarqueeOverlay from '@/components/feature/MarqueeOverlay';

// Aspect ratios this editor supports. Same shape as /appv3's local
// ProtoAspect — kept identically named so the body of the original
// component pastes verbatim without renames.
type ProtoAspect = '1:1' | '4:5' | '2:3' | '9:16' | '16:9' | '1.91:1';

// Cost multipliers per quality tier. Copied verbatim from /appv3 (same
// shape used across the wizard's cost-preview UIs). Editor uses it in
// the Re-render popover.
const QUALITY_MULTIPLIER: Record<Quality, number> = { low: 1, medium: 2, high: 4 };

// Popover panel — outlined card that floats ABOVE the trigger pill in
// the editor footer. Lifted verbatim from /appv3's inline definition so
// the extracted editor doesn't carry a cross-page import dependency.
function FashionPopoverPanel({ title, onClose, align = 'left', children }: {
  title: string;
  onClose: () => void;
  align?: 'left' | 'right';
  children: React.ReactNode;
}) {
  return (
    <div
      className={`
        absolute bottom-full mb-3 z-30
        rounded-2xl bg-white border border-surface-dark/40 shadow-xl shadow-dark/10
        p-4 min-w-[280px]
        ${align === 'right' ? 'right-0' : 'left-0'}
      `}
      role="dialog"
      aria-label={title}
    >
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-xs font-bold uppercase tracking-[0.12em] text-dark">
          {title}
        </h3>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          className="w-6 h-6 rounded-full bg-surface hover:bg-surface-dark/30 flex items-center justify-center text-dark-muted hover:text-dark transition-colors cursor-pointer"
        >
          <i className="ri-close-line text-sm" />
        </button>
      </div>
      <div>{children}</div>
    </div>
  );
}

// Outlined clickable pill in the right-panel footer. Opens a popover
// ABOVE on click; click-outside closes. Lifted verbatim from /appv3.
function FashionFooterPill({
  label, icon, isOpen, onToggle, onClose, popoverTitle, align = 'left', children,
}: {
  label: string;
  icon?: string;
  isOpen: boolean;
  onToggle: () => void;
  onClose: () => void;
  popoverTitle: string;
  align?: 'left' | 'right';
  children: React.ReactNode;
}) {
  const containerRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!isOpen) return;
    const onDown = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        onClose();
      }
    };
    window.addEventListener('mousedown', onDown);
    return () => window.removeEventListener('mousedown', onDown);
  }, [isOpen, onClose]);

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        onClick={onToggle}
        className={`
          h-11 px-4 inline-flex items-center justify-center gap-1.5 rounded-full border
          text-xs font-bold uppercase tracking-[0.08em] transition-all cursor-pointer whitespace-nowrap
          ${isOpen
            ? 'bg-primary text-white border-primary shadow-sm shadow-primary/20'
            : 'bg-white text-dark border-surface-dark/40 hover:border-primary hover:text-primary'}
        `}
      >
        {icon && <i className={`${icon} text-base`} />}
        {label}
      </button>
      {isOpen && (
        <FashionPopoverPanel title={popoverTitle} onClose={onClose} align={align}>
          {children}
        </FashionPopoverPanel>
      )}
    </div>
  );
}

// StudioFinishEditor (Jun 12 v2.1) — the render panel's RESULT state.
// Layout per the user's Figma prototype (STRUCTURE only — styling uses the
// app's existing design system, not the wireframe's black panel):
//   LEFT (589px) — ONE white panel (same chrome as every Studio panel):
//     • Layer rail: Logo tile + CTA tile (each tile IS its layer — add on
//       first click, select afterwards; no separate "add" buttons), then
//       the text line items with a ghost "+ Text" row LAST (using the
//       last item spawns the next one below — no wasted vertical space).
//     • Property editor for the selected layer (text + AI fill, font
//       size, weight, case, color — or size/opacity/tint for the logo).
//     • "Edit with prompt…" + EDIT (1 CR).
//     • ONE action row: BACK TO DESIGN · ADJ QUALITY · SCHEDULE · DOWNLOAD.
//   RIGHT (843px) — ONLY the rendered image (+ frame thumbs on carousels).
// Drag/clamp/scale machinery is the canonical StepText implementation
// (memory: feedback_text_drag) copied verbatim.
// ===========================================================================

// width/height ratio per aspect — caps the preview height (~850px) while
// width anchors the aspect box (gotcha #50 family).
const FINISH_ASPECT_RATIO_W_OVER_H: Record<ProtoAspect, number> = {
  '1:1': 1,
  '4:5': 0.8,
  '2:3': 2 / 3,
  '9:16': 0.5625,
  '16:9': 16 / 9,
  '1.91:1': 1.91,
};

// Layer rail row — one per filled Logo/CTA/Text layer. Whole row selects
// the layer; the trash icon on the right deletes it. The delete icon is
// always visible so users never miss the Delete-Layer affordance hidden
// down in the property panel (Jun 17 user feedback).
function RailFilledRow({
  selected,
  onSelect,
  onDelete,
  icon,
  label,
  deleteTitle,
}: {
  selected: boolean;
  onSelect: () => void;
  onDelete: () => void;
  icon: string;
  label: string;
  deleteTitle: string;
}) {
  return (
    <div
      className={`
        w-full rounded-lg border text-xs font-bold uppercase tracking-wide transition-all
        flex items-stretch
        ${selected
          ? 'bg-primary-light text-primary border-primary/40'
          : 'bg-surface text-dark border-transparent hover:bg-surface-dark/40'}
      `}
    >
      <button
        type="button"
        onClick={onSelect}
        className="flex items-center min-w-0 flex-1 text-left px-3 py-2.5 cursor-pointer bg-transparent"
      >
        {icon ? <i className={`${icon} mr-1 flex-shrink-0`} /> : null}
        <span className="truncate">{label}</span>
      </button>
      <button
        type="button"
        onClick={onDelete}
        title={deleteTitle}
        aria-label={deleteTitle}
        className="flex-shrink-0 px-2.5 flex items-center justify-center text-dark-muted/60 hover:text-red-500 transition-colors cursor-pointer"
      >
        <i className="ri-delete-bin-line text-sm" />
      </button>
    </div>
  );
}

function StudioFinishEditor({
  frames, aspectRatio, activeFrameIdx, onActiveFrameChange,
  overlays, onOverlaysChange,
  logoOverlays, onLogoOverlaysChange,
  brandLogoUrl, brandColors,
  suggestedHeadline, suggestedTagline,
  editPrompt, onEditPromptChange, promptEditing, editError, onPromptEdit,
  isFree, frameCount,
  retryOpen, onToggleRetry, onCloseRetry, onRegenerateWithQuality,
  saveState, onRetrySave,
  onSchedule, onDownload, downloading,
  onManualSave, creativeDirty,
  showRetry = true,
  retryRendering = null,
  // Marquee edit (Jun 24) — optional. When all 3 are provided, a "Marquee
  // edit" toggle button appears in the action row. Parent owns the network
  // state (applying flag + history count) so the editor stays presentation-
  // only. Undo only renders when undoStackSize > 0.
  marqueeApplying = false,
  marqueeUndoing = false,
  marqueeHistoryCount = 0,
  onMarqueeApply,
  onMarqueeUndo,
  // Marquee preview → Keep/Discard (Jul 17). When active, the displayed frame
  // is a rendered-but-unsaved region edit; we show a confirm bar over the image
  // and lock the other mutating controls until the user decides.
  marqueePreviewActive = false,
  marqueeKeepSaving = false,
  onMarqueeKeep,
  onMarqueeDiscard,
  // Edit Text (Jul 14) — optional. When onEditText is provided, an "Edit Text"
  // pill appears in the action row that lifts baked-in text into editable
  // overlays. Parent owns the network call + charge; hides the button (by not
  // passing onEditText) once the post is already editable.
  onEditText,
  editingText = false,
  editTextCredits = 2,
  editTextDisabled = false,
  editTextTitle,
}: {
  frames: string[];
  aspectRatio: ProtoAspect;
  activeFrameIdx: number;
  onActiveFrameChange: (i: number) => void;
  overlays: TextOverlay[];
  onOverlaysChange: (o: TextOverlay[]) => void;
  logoOverlays: LogoOverlay[];
  onLogoOverlaysChange: (l: LogoOverlay[]) => void;
  brandLogoUrl: string | null;
  brandColors: string[];
  suggestedHeadline: string | null;
  suggestedTagline: string | null;
  editPrompt: string;
  onEditPromptChange: (v: string) => void;
  promptEditing: boolean;
  editError: string | null;
  onPromptEdit: () => void;
  isFree: boolean;
  frameCount: number;
  retryOpen: boolean;
  onToggleRetry: () => void;
  onCloseRetry: () => void;
  onRegenerateWithQuality: (q: Quality) => void;
  saveState: 'idle' | 'saving' | 'saved' | 'error';
  onRetrySave: () => void;
  onSchedule: () => void;
  onDownload: () => void;
  downloading: boolean;
  onManualSave: () => void;
  creativeDirty: boolean;
  /** Show the Re-render quality picker (Low/Med/High) in the action row. */
  showRetry?: boolean;
  /** Quality tier currently rendering, or null when idle. When set, the
   *  Re-render button shows a spinner + "Rendering high…" label, and
   *  Post/Download/Save edits all disable so the user can't double-spend
   *  or leave mid-render without warning. */
  retryRendering?: 'low' | 'medium' | 'high' | null;
  // ── Marquee edit (Jun 24) ────────────────────────────────────────────
  /** Spinner state on the marquee Apply button while the backend renders. */
  marqueeApplying?: boolean;
  /** Spinner state on the Undo button while the backend pops history. */
  marqueeUndoing?: boolean;
  /** posts.base_image_path_history length — drives Undo button visibility. */
  marqueeHistoryCount?: number;
  /** Apply the marquee edit. Called with fractional 0-1 rect + instruction.
   *  Parent should call /api/creatives/:id/marquee-edit + update Post. */
  onMarqueeApply?: (
    rect: { x: number; y: number; w: number; h: number },
    instruction: string,
  ) => void;
  /** Undo the last marquee edit. Parent calls /api/creatives/:id/marquee-undo. */
  onMarqueeUndo?: () => void;
  /** A rendered-but-unsaved marquee edit is awaiting the user's decision. */
  marqueePreviewActive?: boolean;
  /** True while Keep's save-as-version is in flight (spinner on the bar). */
  marqueeKeepSaving?: boolean;
  /** Keep the previewed edit — parent forks a new version. */
  onMarqueeKeep?: () => void;
  /** Discard the previewed edit — parent reverts to the original image. */
  onMarqueeDiscard?: () => void;
  // ── Edit Text (Jul 14) ───────────────────────────────────────────────
  /** Lift baked-in text into editable overlays. When provided, an "Edit Text"
   *  pill renders in the action row. Omit to hide (already editable / N/A). */
  onEditText?: () => void;
  /** Spinner state while the backend removes text + reads the overlays. */
  editingText?: boolean;
  /** Credit cost shown on the pill (2 single, 8 carousel). */
  editTextCredits?: number;
  /** Disable the pill (e.g. insufficient balance). */
  editTextDisabled?: boolean;
  /** Tooltip for the pill (e.g. "Need 8 credits"). */
  editTextTitle?: string;
}) {
  const [activeId, setActiveId] = useState<string | null>(null);
  const [draggingId, setDraggingId] = useState<string | null>(null);
  const [previewW, setPreviewW] = useState(0);
  // Marquee mode (Jun 24) — when true, the preview captures mouse events
  // for rect drawing instead of overlay drag. The MarqueeOverlay renders
  // inside the preview and exits via its own × button OR after a
  // successful apply (auto-cleared via the history-count effect below).
  const [marqueeMode, setMarqueeMode] = useState(false);
  const marqueeEnabled = Boolean(onMarqueeApply);
  // Auto-exit marquee mode after a successful apply: history count grows
  // every time the backend persists an edit. We watch the count and clear
  // the rect by toggling mode off + on so the overlay state resets. (The
  // overlay resets its internal rect when `enabled` flips false → true.)
  const lastSeenHistoryRef = useRef(marqueeHistoryCount);
  useEffect(() => {
    if (marqueeHistoryCount > lastSeenHistoryRef.current) {
      // Successful apply → reset the overlay's drawn rect by toggling mode.
      // Keep marqueeMode true so the user can immediately draw another rect
      // (common workflow: remove text → add new text → tweak the result).
      // The MarqueeOverlay clears its internal rect+instruction on every
      // disable, so flipping off and on resets it.
      setMarqueeMode(false);
      const t = setTimeout(() => setMarqueeMode(true), 50);
      lastSeenHistoryRef.current = marqueeHistoryCount;
      return () => clearTimeout(t);
    }
    lastSeenHistoryRef.current = marqueeHistoryCount;
  }, [marqueeHistoryCount]);
  // Stage (the filled right-panel box) measured size — the image box is
  // sized to fit INSIDE it (object-contain math) so non-1:1 aspects letterbox
  // on the stage instead of leaving a gap next to the editor (point 5).
  const [stageSize, setStageSize] = useState({ w: 0, h: 0 });

  // Refs to avoid stale closures inside long-lived drag handlers — same
  // shape as StepText.
  const overlaysRef = useRef(overlays); overlaysRef.current = overlays;
  const logoOverlaysRef = useRef(logoOverlays); logoOverlaysRef.current = logoOverlays;
  const activeIdRef = useRef(activeId); activeIdRef.current = activeId;
  const draggingLogoRef = useRef(false);
  const previewRef = useRef<HTMLDivElement>(null);
  const stageRef = useRef<HTMLDivElement>(null);
  const dragBoxRef = useRef<{ w: number; h: number } | null>(null);
  const dragOffsetRef = useRef<{ dx: number; dy: number } | null>(null);

  // ---- Drag-to-position (canonical StepText implementation) ----
  useEffect(() => {
    if (!draggingId) return;
    const updateFromPoint = (clientX: number, clientY: number) => {
      const rect = previewRef.current?.getBoundingClientRect();
      if (!rect || rect.width === 0 || rect.height === 0) return;

      // Logo path — top-left anchor model, clamp directly with box dims.
      if (draggingLogoRef.current) {
        const logo = logoOverlaysRef.current.find((l) => l.id === draggingId);
        if (!logo) return;
        const cursorX = (clientX - rect.left) / rect.width;
        const cursorY = (clientY - rect.top) / rect.height;
        const off = dragOffsetRef.current;
        const targetX = off ? cursorX - off.dx : cursorX;
        const targetY = off ? cursorY - off.dy : cursorY;
        const box = dragBoxRef.current;
        const wFrac = box ? box.w / rect.width : logo.widthFrac;
        const hFrac = box ? box.h / rect.height : logo.widthFrac;
        const clampedX = Math.max(0, Math.min(1 - wFrac, targetX));
        const clampedY = Math.max(0, Math.min(1 - hFrac, targetY));
        onLogoOverlaysChange(
          logoOverlaysRef.current.map((l) =>
            l.id === draggingId
              ? { ...l, x: clampedX, y: clampedY, positionId: '' }
              : l,
          ),
        );
        return;
      }

      // Text path — anchor-aware clamp by the rendered box bounds so the
      // block can never escape the frame.
      const dragged = overlaysRef.current.find((o) => o.id === draggingId);
      if (!dragged) return;
      const cursorX = (clientX - rect.left) / rect.width;
      const cursorY = (clientY - rect.top) / rect.height;
      const off = dragOffsetRef.current;
      const targetX = off ? cursorX - off.dx : cursorX;
      const targetY = off ? cursorY - off.dy : cursorY;
      const box = dragBoxRef.current;
      const wFrac = box ? box.w / rect.width : 0;
      const hFrac = box ? box.h / rect.height : 0;
      const { x, y } = clampAnchor(targetX, targetY, wFrac, hFrac, dragged.anchor);
      onOverlaysChange(
        overlaysRef.current.map((o) =>
          o.id === draggingId ? { ...o, x, y, positionId: '' } : o,
        ),
      );
    };
    const onMouseMove = (e: MouseEvent) => updateFromPoint(e.clientX, e.clientY);
    const onTouchMove = (e: TouchEvent) => {
      const t = e.touches[0];
      if (t) updateFromPoint(t.clientX, t.clientY);
    };
    const onUp = () => {
      setDraggingId(null);
      dragBoxRef.current = null;
      dragOffsetRef.current = null;
      draggingLogoRef.current = false;
    };
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onUp);
    window.addEventListener('touchmove', onTouchMove, { passive: true });
    window.addEventListener('touchend', onUp);
    return () => {
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onUp);
      window.removeEventListener('touchmove', onTouchMove);
      window.removeEventListener('touchend', onUp);
    };
  }, [draggingId, onOverlaysChange, onLogoOverlaysChange]);

  // ---- Preview width → font scale (DOM wrap ≡ canvas wrap) ----
  useEffect(() => {
    const el = previewRef.current;
    if (!el) return;
    const update = () => setPreviewW(el.getBoundingClientRect().width);
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  const previewFontScale = previewW > 0 ? previewW / REFERENCE_CANVAS_WIDTH : 1;

  // ---- Stage size → image box size (object-contain inside the filled stage) ----
  useEffect(() => {
    const el = stageRef.current;
    if (!el) return;
    const update = () => {
      const r = el.getBoundingClientRect();
      setStageSize({ w: r.width, h: r.height });
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // ---- Derived per-frame data ----
  // CTA is a text layer with an id prefix — the rail's CTA tile maps to
  // it 1:1 (one CTA per frame); everything else is a free text item.
  const currentOverlays = overlays.filter((o) => o.imageIndex === activeFrameIdx);
  const ctaLayer = currentOverlays.find((o) => o.id.startsWith('cta-')) || null;
  const textLayers = currentOverlays.filter((o) => !o.id.startsWith('cta-'));
  const currentLogos = logoOverlays.filter((l) => l.imageIndex === activeFrameIdx);
  const logoLayer = currentLogos[0] || null;
  const refH = aspectToRefHeight(aspectRatio);
  const resolvedCurrent = useMemo(
    () => resolveStackedLayers(currentOverlays, REFERENCE_CANVAS_WIDTH, refH) as TextOverlay[],
    [currentOverlays, refH],
  );
  const activeOverlay = currentOverlays.find((o) => o.id === activeId) || null;
  const activeLogo = currentLogos.find((l) => l.id === activeId) || null;
  const activeImage = frames[activeFrameIdx] || '';

  // Any image-transforming op in flight — a quality re-render, an AI "Apply
  // edit", or a marquee region edit/undo. While ANY runs we lock the mutating
  // controls (no stacking a second op; no save/post/download of a base that's
  // about to change) AND drape a loading veil over the image stage so the
  // progress isn't only on one button. See renderBusyLabel for the caption.
  const renderBusy =
    retryRendering !== null || promptEditing || marqueeApplying || marqueeUndoing;
  // `mutating` gates the buttons — it ALSO covers the marquee preview-pending
  // state (a rendered edit awaiting Keep/Discard). `renderBusy` (the spinner
  // veil) deliberately excludes it, since during the preview we show the
  // result + a confirm bar, not a loading spinner.
  const mutating = renderBusy || marqueePreviewActive;
  const renderBusyLabel = retryRendering
    ? `Rendering ${retryRendering}…`
    : promptEditing
      ? 'Applying your edit…'
      : marqueeUndoing
        ? 'Undoing…'
        : marqueeApplying
          ? 'Applying region edit…'
          : '';

  // Complementary text-color suggestions sampled from the active frame's
  // background. Recomputed whenever the frame image changes (prompt-edit,
  // frame switch, new render). [] when the image is all-neutral.
  const [bgColors, setBgColors] = useState<string[]>([]);
  useEffect(() => {
    let cancelled = false;
    if (!activeImage) { setBgColors([]); return; }
    suggestTextColorsFromImage(activeImage, 3)
      .then((cols) => { if (!cancelled) setBgColors(cols); })
      .catch(() => { if (!cancelled) setBgColors([]); });
    return () => { cancelled = true; };
  }, [activeImage]);

  const logoFilterCss = (tint: LogoTint | undefined): string | undefined => {
    if (tint === 'black') return 'brightness(0)';
    if (tint === 'white') return 'brightness(0) invert(1)';
    return undefined;
  };

  // ---- Layer mutation ----
  const updateOverlay = (id: string, updates: Partial<TextOverlay>) => {
    onOverlaysChange(overlaysRef.current.map((o) => (o.id === id ? { ...o, ...updates } : o)));
  };
  const updateLogo = (id: string, updates: Partial<LogoOverlay>) => {
    onLogoOverlaysChange(
      logoOverlaysRef.current.map((l) => (l.id === id ? { ...l, ...updates } : l)),
    );
  };
  // Quick-place the logo at one of the 3×3 inset corners/edges. Mirrors
  // the text/CTA placement grid but uses the logo's top-left anchor
  // model (logo.x/logo.y = top-left fractional coords). Height fraction
  // is read from the rendered <img> at click time (logo's height comes
  // from natural-aspect, so we can't compute it from widthFrac alone).
  const placeLogoAt = (posId: string) => {
    const logo = logoOverlaysRef.current.find((l) => l.id === activeIdRef.current);
    if (!logo) return;
    const preview = previewRef.current;
    if (!preview) return;
    const previewRect = preview.getBoundingClientRect();
    if (previewRect.width === 0 || previewRect.height === 0) return;
    const logoEl = preview.querySelector(
      `[data-logo-id="${logo.id}"]`,
    ) as HTMLElement | null;
    if (!logoEl) return;
    const logoRect = logoEl.getBoundingClientRect();
    const wFrac = logoRect.width / previewRect.width;
    const hFrac = logoRect.height / previewRect.height;
    const pad = OVERLAY_PAD_FRAC;
    const left = posId.includes('left');
    const right = posId.includes('right');
    const top = posId.startsWith('top');
    const bottom = posId.startsWith('bottom');
    const x = left ? pad : right ? 1 - pad - wFrac : 0.5 - wFrac / 2;
    const y = top ? pad : bottom ? 1 - pad - hFrac : 0.5 - hFrac / 2;
    updateLogo(logo.id, {
      x: Math.max(0, Math.min(1 - wFrac, x)),
      y: Math.max(0, Math.min(1 - hFrac, y)),
      positionId: posId,
    });
  };
  const removeLayer = (id: string) => {
    onOverlaysChange(overlaysRef.current.filter((o) => o.id !== id));
    onLogoOverlaysChange(logoOverlaysRef.current.filter((l) => l.id !== id));
    if (activeIdRef.current === id) setActiveId(null);
  };

  // ---- Layer creation ----
  // New text items default to the next UNUSED piece of AI copy (headline
  // first, then tagline) so adding layers builds the post instead of
  // dropping placeholders.
  const nextDefaultText = (): string => {
    const used = new Set(textLayers.map((o) => o.text));
    if (suggestedHeadline && !used.has(suggestedHeadline)) return suggestedHeadline;
    if (suggestedTagline && !used.has(suggestedTagline)) return suggestedTagline;
    return 'Your text here';
  };
  const addTextLayer = () => {
    const next: TextOverlay = {
      kind: 'text',
      id: `text-${Date.now()}`,
      imageIndex: activeFrameIdx,
      text: nextDefaultText(),
      x: 0.5,
      y: 0.4,
      anchor: 'mc',
      maxWidthFrac: 0.82,
      textAlign: 'center',
      fontSize: 64,
      fontWeight: 'bold',
      fontFamily: 'Manrope',
      color: '#FFFFFF',
      textTransform: 'uppercase',
      maxLines: 3,
    };
    onOverlaysChange([...overlaysRef.current, next]);
    setActiveId(next.id);
  };
  const addCtaLayer = () => {
    if (ctaLayer) { setActiveId(ctaLayer.id); return; }
    const next: TextOverlay = {
      kind: 'text',
      id: `cta-${Date.now()}`,
      imageIndex: activeFrameIdx,
      text: 'SHOP NOW',
      x: 0.5,
      y: 0.84,
      anchor: 'mc',
      maxWidthFrac: 0.6,
      textAlign: 'center',
      fontSize: 34,
      fontWeight: 'bold',
      fontFamily: 'Manrope',
      color: '#FFFFFF',
      textTransform: 'uppercase',
      maxLines: 1,
      backgroundColor: '#0F0F1E',
      backgroundPadding: 18,
    };
    onOverlaysChange([...overlaysRef.current, next]);
    setActiveId(next.id);
  };
  const addLogoLayer = () => {
    if (logoLayer) { setActiveId(logoLayer.id); return; }
    if (!brandLogoUrl) return;
    const defaultW = 0.18;
    const next: LogoOverlay = {
      id: `logo-${Date.now()}`,
      imageIndex: activeFrameIdx,
      src: brandLogoUrl,
      x: 1 - defaultW - 0.04,
      y: 1 - defaultW - 0.04,
      widthFrac: defaultW,
      opacity: 1,
      tint: 'original',
    };
    onLogoOverlaysChange([...logoOverlaysRef.current, next]);
    setActiveId(next.id);
  };

  // AI fill: first press inserts the AI headline, next press the tagline
  // (toggles when both exist).
  const aiFillText = () => {
    if (!activeOverlay || !suggestedHeadline) return;
    const next = activeOverlay.text === suggestedHeadline && suggestedTagline
      ? suggestedTagline
      : suggestedHeadline;
    updateOverlay(activeOverlay.id, { text: next });
  };

  const WEIGHT_OPTIONS: { value: string; cls: string; title: string }[] = [
    { value: 'normal', cls: 'font-normal', title: 'Regular' },
    { value: 'semibold', cls: 'font-semibold', title: 'Semibold' },
    { value: 'bold', cls: 'font-extrabold', title: 'Bold' },
  ];
  const CASE_OPTIONS: { value: TextOverlay['textTransform']; label: string; title: string }[] = [
    { value: 'none', label: 'Aa', title: 'As typed' },
    { value: 'uppercase', label: 'AA', title: 'UPPERCASE' },
    { value: 'lowercase', label: 'aa', title: 'lowercase' },
  ];
  // Font families offered in the text editor. All self-hosted (fonts.ts) so
  // they're loaded for both the DOM preview and the canvas flatten; matches
  // the social-template-builder's authoring set. `fontFamily` is already a
  // field on TextOverlay and honored by both renderers.
  const FONT_OPTIONS = [
    'Manrope', 'Inter', 'DM Sans', 'Archivo', 'Space Grotesk', 'Space Mono',
    'JetBrains Mono', 'Fraunces', 'Cormorant Garamond', 'Bodoni Moda',
    'Playfair Display', 'Instrument Serif', 'Bebas Neue', 'Anton',
    // Typewolf "40 Best Google Fonts" set
    'Chivo', 'Work Sans', 'Syne', 'Libre Franklin', 'Cormorant', 'Fira Sans',
    'Eczar', 'Alegreya Sans', 'Alegreya', 'Source Sans Pro', 'Source Serif Pro',
    'Roboto', 'Inknut Antiqua', 'BioRhyme', 'Poppins', 'Archivo Narrow',
    'Libre Baskerville', 'Karla', 'Lora', 'Proza Libre', 'Spectral',
    'IBM Plex Sans', 'Montserrat', 'Lato', 'PT Sans', 'PT Serif', 'Cardo',
    'Neuton', 'Rubik', 'Open Sans', 'Inconsolata', 'Raleway', 'Merriweather',
  ];
  // 3×3 placement grid (rows top→bottom, cols left→center→right). Each id is
  // fed to snapForPosition() → {x, y, anchor, textAlign} so the text snaps to
  // the inset edge/corner and aligns inward. Mirrors the Animation placement tool.
  const PLACEMENT_CELLS = [
    'top-left', 'top-center', 'top-right',
    'middle-left', 'middle-center', 'middle-right',
    'bottom-left', 'bottom-center', 'bottom-right',
  ];
  const retryOptions: { q: Quality; label: string }[] = [
    { q: 'low', label: 'Low' },
    { q: 'medium', label: 'Medium' },
    { q: 'high', label: 'High' },
  ];

  // App design system tokens (NOT the wireframe's black panel).
  const eyebrowClass = 'text-[10px] font-bold uppercase tracking-[0.18em] text-dark-muted';
  const actionPillClass = 'h-11 px-4 inline-flex items-center justify-center gap-1.5 rounded-full bg-white border border-surface-dark/40 hover:border-primary hover:text-primary text-dark text-xs font-bold uppercase tracking-[0.08em] transition-all cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed';
  const chipClass = (active: boolean) => `
    w-9 h-9 inline-flex items-center justify-center rounded-lg border text-sm transition-all cursor-pointer
    ${active
      ? 'bg-primary-light text-primary border-primary'
      : 'bg-white border-surface-dark/40 text-dark hover:border-primary/40'}
  `;
  const railRowClass = (selected: boolean) => `
    w-full text-left px-3 py-2.5 rounded-lg text-xs font-bold uppercase tracking-wide truncate transition-all cursor-pointer border
    ${selected
      ? 'bg-primary-light text-primary border-primary/40'
      : 'bg-surface text-dark border-transparent hover:bg-surface-dark/40'}
  `;
  const railGhostClass = 'w-full text-left px-3 py-2.5 rounded-lg text-xs font-bold uppercase tracking-wide border-2 border-dashed border-surface-dark/60 text-dark-muted hover:border-primary/50 hover:text-primary transition-all cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed';

  // Image box size = the largest that fits INSIDE the measured stage while
  // keeping aspect (object-contain). The stage is the filled right-panel box
  // (point 5) that fills the 800px column; non-1:1 images letterbox on the
  // stage bg instead of leaving a gap. Falls back before the first measure.
  const aspectWOverH = FINISH_ASPECT_RATIO_W_OVER_H[aspectRatio] ?? 1;
  const boxW = stageSize.w > 0 && stageSize.h > 0
    ? Math.floor(Math.min(stageSize.w, stageSize.h * aspectWOverH))
    : Math.round(700 * aspectWOverH);

  return (
    // 589 + 20 (gap-5) + 843 = 1452 = the page container's inner width
    // (user-locked column split). Fixed 800px height so both columns fill it
    // and the right stage can letterbox non-1:1 images without a gap.
    /* Image-LEFT / controls-RIGHT (Jun 17 user-locked swap). Same content,
       visual order flipped via grid template + order utilities so the DOM
       (and refs/state) stays unchanged. */
    <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,843px)_589px] gap-5 items-stretch xl:h-[800px]">

      {/* ============ Controls panel (now on the RIGHT via order) ============ */}
      <div className="order-2 xl:order-2 rounded-2xl bg-white shadow-[0_0_0_4px_#ffffff,0_25px_50px_-12px_rgba(15,15,30,0.18)] p-5 flex flex-col gap-4 min-h-0">

        {/* Editor — layer rail + property panel. flex-1 so the editor
            absorbs all the vertical room the image column provides. */}
        <div className="flex gap-3 flex-1 min-h-0">

          {/* Layer rail. Logo + CTA are PERSISTENT tiles (the tile IS the
              layer — first click creates, after that it selects). Text
              line items follow, ghost "+ Text" row LAST: using it spawns
              the next one below. */}
          <div className="w-[170px] flex-shrink-0 flex flex-col gap-2 overflow-y-auto sidebar-scroll">
            {logoLayer ? (
              <RailFilledRow
                selected={activeId === logoLayer.id}
                onSelect={() => setActiveId(logoLayer.id)}
                onDelete={() => removeLayer(logoLayer.id)}
                icon="ri-vip-crown-line"
                label="Logo"
                deleteTitle="Remove logo"
              />
            ) : (
              <button
                type="button"
                onClick={addLogoLayer}
                disabled={!brandLogoUrl}
                title={!brandLogoUrl
                  ? 'Set up your logo in My Brand first'
                  : 'Add your brand logo'}
                className={railGhostClass}
              >
                <i className="ri-add-line mr-1" />
                Logo
              </button>
            )}

            {ctaLayer ? (
              <RailFilledRow
                selected={activeId === ctaLayer.id}
                onSelect={() => setActiveId(ctaLayer.id)}
                onDelete={() => removeLayer(ctaLayer.id)}
                icon=""
                label={ctaLayer.text || 'CTA'}
                deleteTitle="Remove CTA"
              />
            ) : (
              <button
                type="button"
                onClick={addCtaLayer}
                title="Call-to-action pill — drag it on the image to reposition"
                className={railGhostClass}
              >
                <i className="ri-add-line mr-1" />
                CTA
              </button>
            )}

            {textLayers.map((o) => (
              <RailFilledRow
                key={o.id}
                selected={activeId === o.id}
                onSelect={() => setActiveId(o.id)}
                onDelete={() => removeLayer(o.id)}
                icon=""
                label={o.text || 'Text'}
                deleteTitle="Remove text"
              />
            ))}
            {/* Ghost row LAST — becomes a real text item on click, and a
                fresh ghost appears below it. */}
            <button type="button" onClick={addTextLayer} className={railGhostClass}>
              <i className="ri-add-line mr-1" />
              Text
            </button>
          </div>

          {/* Property panel for the selected layer — surface-tinted card
              (matches the filter-bar family), full remaining width. */}
          <div className="flex-1 min-w-0 rounded-xl bg-surface/60 border border-surface-dark/30 p-4 flex flex-col overflow-y-auto sidebar-scroll">
            {activeLogo ? (
              <>
                <p className={eyebrowClass}>Size</p>
                <input
                  type="range"
                  min={5}
                  max={50}
                  value={Math.round(activeLogo.widthFrac * 100)}
                  onChange={(e) => updateLogo(activeLogo.id, { widthFrac: Number(e.target.value) / 100 })}
                  className="w-full accent-primary mt-2"
                />
                <p className={`${eyebrowClass} mt-4`}>Opacity</p>
                <input
                  type="range"
                  min={20}
                  max={100}
                  value={Math.round(activeLogo.opacity * 100)}
                  onChange={(e) => updateLogo(activeLogo.id, { opacity: Number(e.target.value) / 100 })}
                  className="w-full accent-primary mt-2"
                />
                <p className={`${eyebrowClass} mt-4 mb-2`}>Color</p>
                <div className="flex gap-2">
                  {(['original', 'black', 'white'] as LogoTint[]).map((t) => (
                    <button
                      key={t}
                      type="button"
                      onClick={() => updateLogo(activeLogo.id, { tint: t })}
                      className={`px-3 py-1.5 rounded-lg border text-xs font-bold capitalize transition-all cursor-pointer ${
                        (activeLogo.tint ?? 'original') === t
                          ? 'bg-primary-light text-primary border-primary'
                          : 'bg-white border-surface-dark/40 text-dark hover:border-primary/40'
                      }`}
                    >
                      {t}
                    </button>
                  ))}
                </div>
                {/* Placement moved below Color (Jun 17) so the property
                    panel order matches the text/CTA panel: content first,
                    sizing/styling next, anchor placement last. */}
                <p className={`${eyebrowClass} mt-4 mb-2`}>Placement</p>
                <div className="inline-grid grid-cols-3 gap-1 p-1 rounded-lg bg-surface border border-surface-dark/40 self-start">
                  {PLACEMENT_CELLS.map((pos) => {
                    const active = (activeLogo.positionId ?? '') === pos;
                    return (
                      <button
                        key={pos}
                        type="button"
                        title={pos.replace('-', ' ')}
                        aria-label={`Place logo ${pos.replace('-', ' ')}`}
                        onClick={() => placeLogoAt(pos)}
                        className={`w-7 h-7 rounded flex items-center justify-center transition-colors cursor-pointer ${active ? 'bg-primary text-white' : 'bg-white text-dark-muted/50 hover:text-primary'}`}
                      >
                        <span className={`block w-2 h-2 rounded-full ${active ? 'bg-white' : 'bg-current'}`} />
                      </button>
                    );
                  })}
                </div>
                {/* In-panel "Delete layer" removed Jun 17 — the trash
                    icon next to "Logo" in the rail is the only delete
                    affordance now (always visible, never scrolled out). */}
              </>
            ) : activeOverlay ? (
              <>
                <p className={`${eyebrowClass} mb-2`}>
                  Text content{suggestedHeadline ? ' + AI generate' : ''}
                </p>
                <div className="flex gap-1.5">
                  <input
                    type="text"
                    value={activeOverlay.text}
                    onChange={(e) => updateOverlay(activeOverlay.id, { text: e.target.value })}
                    className="flex-1 min-w-0 h-10 px-3 rounded-lg bg-white border border-dark-muted/40 focus:border-primary focus:outline-none text-sm text-dark placeholder:text-dark-muted/60"
                    placeholder="Type your text…"
                  />
                  {suggestedHeadline && (
                    <button
                      type="button"
                      onClick={aiFillText}
                      title="Fill with AI-written copy (press again for the tagline)"
                      className="w-10 h-10 flex-shrink-0 inline-flex items-center justify-center rounded-lg bg-primary hover:bg-primary-hover text-white transition-all cursor-pointer"
                    >
                      <i className="ri-sparkling-2-line" />
                    </button>
                  )}
                </div>

                <p className={`${eyebrowClass} mt-3 mb-2`}>Font</p>
                <select
                  value={activeOverlay.fontFamily}
                  onChange={(e) => updateOverlay(activeOverlay.id, { fontFamily: e.target.value })}
                  className="w-full h-10 px-3 rounded-lg bg-white border border-dark-muted/40 focus:border-primary focus:outline-none text-sm text-dark cursor-pointer"
                  style={{ fontFamily: `'${activeOverlay.fontFamily}', sans-serif` }}
                >
                  {FONT_OPTIONS.map((f) => (
                    <option key={f} value={f} style={{ fontFamily: `'${f}', sans-serif` }}>
                      {f}
                    </option>
                  ))}
                  {!FONT_OPTIONS.includes(activeOverlay.fontFamily) && (
                    <option value={activeOverlay.fontFamily}>
                      {activeOverlay.fontFamily} (current)
                    </option>
                  )}
                </select>

                <p className={`${eyebrowClass} mt-3`}>Font size</p>
                <input
                  type="range"
                  min={20}
                  max={140}
                  step={2}
                  value={activeOverlay.fontSize}
                  onChange={(e) => updateOverlay(activeOverlay.id, { fontSize: Number(e.target.value) })}
                  className="w-full accent-primary mt-1.5"
                />

                <div className="flex gap-6 mt-3 flex-wrap">
                  <div>
                    <p className={`${eyebrowClass} mb-2`}>Placement</p>
                    <div className="inline-grid grid-cols-3 gap-1 p-1 rounded-lg bg-surface border border-surface-dark/40">
                      {PLACEMENT_CELLS.map((pos) => {
                        const active = activeOverlay.positionId === pos;
                        return (
                          <button
                            key={pos}
                            type="button"
                            title={pos.replace('-', ' ')}
                            aria-label={`Place text ${pos.replace('-', ' ')}`}
                            onClick={() => {
                              const snap = snapForPosition(pos);
                              updateOverlay(activeOverlay.id, {
                                positionId: pos,
                                x: snap.x,
                                y: snap.y,
                                anchor: snap.anchor,
                                textAlign: snap.textAlign,
                              });
                            }}
                            className={`w-7 h-7 rounded flex items-center justify-center transition-colors cursor-pointer ${active ? 'bg-primary text-white' : 'bg-white text-dark-muted/50 hover:text-primary'}`}
                          >
                            <span className={`block w-2 h-2 rounded-full ${active ? 'bg-white' : 'bg-current'}`} />
                          </button>
                        );
                      })}
                    </div>
                  </div>
                  <div className="flex flex-col gap-3">
                    <div>
                      <p className={`${eyebrowClass} mb-2`}>Weight</p>
                      <div className="flex gap-1.5">
                        {WEIGHT_OPTIONS.map((w) => (
                          <button
                            key={w.value}
                            type="button"
                            title={w.title}
                            onClick={() => updateOverlay(activeOverlay.id, { fontWeight: w.value })}
                            className={`${chipClass(activeOverlay.fontWeight === w.value)} ${w.cls}`}
                          >
                            A
                          </button>
                        ))}
                      </div>
                    </div>
                    <div>
                      <p className={`${eyebrowClass} mb-2`}>Case</p>
                      <div className="flex gap-1.5">
                        {CASE_OPTIONS.map((c) => (
                          <button
                            key={c.value}
                            type="button"
                            title={c.title}
                            onClick={() => updateOverlay(activeOverlay.id, { textTransform: c.value })}
                            className={`${chipClass(activeOverlay.textTransform === c.value)} font-bold`}
                          >
                            {c.label}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>

                <p className={`${eyebrowClass} mt-3 mb-2`}>Color</p>
                <div className="flex flex-col gap-2">
                  {[
                    ...(brandColors.length ? [{ key: 'brand', label: 'Brand', colors: brandColors }] : []),
                    { key: 'bw', label: 'B & W', colors: ['#FFFFFF', '#000000'] },
                    ...(bgColors.length ? [{ key: 'bg', label: 'From BG', colors: bgColors }] : []),
                  ].map((group) => (
                    <div key={group.key} className="flex items-center gap-2">
                      <span
                        className="w-14 flex-shrink-0 text-[10px] font-bold uppercase tracking-wide text-dark-muted/70 leading-tight"
                        title={group.key === 'bg' ? 'Complementary colors sampled from the background' : undefined}
                      >
                        {group.label}
                      </span>
                      <div className="flex gap-1.5 flex-wrap">
                        {group.colors.map((c) => (
                          <button
                            key={`${group.key}-${c}`}
                            type="button"
                            onClick={() => updateOverlay(activeOverlay.id, { color: c })}
                            title={c}
                            className={`w-7 h-7 rounded-lg border-2 transition-all cursor-pointer ${
                              activeOverlay.color.toUpperCase() === c.toUpperCase()
                                ? 'border-primary scale-110'
                                : 'border-surface-dark/40 hover:border-primary/40'
                            }`}
                            style={{ backgroundColor: c }}
                          />
                        ))}
                      </div>
                    </div>
                  ))}
                </div>

                {/* In-panel "Delete layer" removed Jun 17 — trash icon
                    on the layer rail row is the single source of truth. */}
              </>
            ) : (
              <div className="flex-1 flex items-center justify-center text-center px-4">
                <p className="text-sm text-dark-muted leading-relaxed">
                  Select a layer on the left — or add one — then drag it
                  anywhere on the image.
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Edit with AI (point 2) — punched-out, accent-tinted card so the
            prompt-edit affordance is impossible to miss. Sparkle icon + label
            + a primary EDIT button. */}
        <div className="flex-shrink-0 rounded-2xl border-2 border-primary/30 bg-primary-light/40 p-3.5">
          <div className="flex items-center gap-2.5 mb-2.5">
            <span className="w-8 h-8 flex-shrink-0 rounded-lg bg-primary text-white flex items-center justify-center shadow-sm shadow-primary/30">
              <i className="ri-sparkling-2-fill text-base" />
            </span>
            <div className="min-w-0">
              <p className="text-sm font-bold text-dark leading-none">Edit with AI</p>
              <p className="text-[11px] text-dark-muted mt-1 leading-tight">
                Describe a change — the scene transforms, your text stays on top.
              </p>
            </div>
          </div>
          <textarea
            value={editPrompt}
            onChange={(e) => onEditPromptChange(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                onPromptEdit();
              }
            }}
            disabled={mutating}
            rows={2}
            placeholder={frames.length > 1
              ? `e.g. "make the background a sunset" — frame ${activeFrameIdx + 1}`
              : 'e.g. "make the background a sunset", "add soft studio lighting"…'}
            className="w-full px-3.5 py-2.5 rounded-xl bg-white border border-surface-dark/40 focus:border-primary focus:outline-none text-sm text-dark placeholder:text-dark-muted/60 resize-none disabled:opacity-60"
          />
          {editError && (
            <p className="text-xs text-red-600 mt-1.5">{editError}</p>
          )}
          <button
            type="button"
            onClick={onPromptEdit}
            disabled={mutating || !editPrompt.trim()}
            title="AI-edit the image base — your text layers stay on top"
            className="mt-2 w-full h-11 inline-flex items-center justify-center gap-2 rounded-full bg-primary hover:bg-primary-hover text-white text-sm font-bold uppercase tracking-[0.08em] shadow-md shadow-primary/25 transition-all cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {promptEditing ? (
              <><i className="ri-loader-4-line animate-spin text-base" /> Editing…</>
            ) : (
              <><i className="ri-magic-line text-base" /> Apply edit · 1 cr</>
            )}
          </button>
        </div>

        {/* Action row (Jun 17 cleanup). Back-to-design dropped — this
            editor is used from /creatives/:id which already has a Back
            link to My Creatives in the page header. Order: Re-render
            (popover) · Post · Download · Save edits (filled primary,
            rightmost — emphasized when dirty). */}
        <div className="flex-shrink-0 flex items-center gap-2 flex-wrap">
          {showRetry && (
            retryRendering ? (
              // In-flight: replace the popover button with a non-clickable
              // status pill that reads "Rendering <quality>…" — so the user
              // knows clicks landed + something is happening. Same h-11 +
              // rounded-full + bg-primary shape as the popover trigger so
              // there's no layout shift on enter/exit.
              <span
                aria-live="polite"
                className="h-11 px-4 inline-flex items-center justify-center gap-1.5 rounded-full border text-xs font-bold uppercase tracking-[0.08em] whitespace-nowrap bg-primary text-white border-primary shadow-sm shadow-primary/20"
              >
                <i className="ri-loader-4-line animate-spin text-base" />
                Rendering {retryRendering}…
              </span>
            ) : mutating ? (
              // Another op (Apply edit / marquee / pending preview) is active —
              // lock the Re-render trigger so renders can't stack. Grayed.
              <span className="h-11 px-4 inline-flex items-center justify-center gap-1.5 rounded-full border text-xs font-bold uppercase tracking-[0.08em] whitespace-nowrap bg-white text-dark border-surface-dark/40 opacity-50 cursor-not-allowed">
                <i className="ri-refresh-line text-base" />
                Re-render
              </span>
            ) : (
              <FashionFooterPill
                label="Re-render"
                icon="ri-refresh-line"
                isOpen={retryOpen}
                onToggle={onToggleRetry}
                onClose={onCloseRetry}
                popoverTitle="Re-render at a different quality"
                align="left"
              >
                <div className="space-y-2 min-w-[300px]">
                  <p className="text-xs text-dark-muted leading-relaxed">
                    Re-renders your current design — including any edits made since
                    this render — at the quality you pick. Each run charges credits.
                  </p>
                  {retryOptions.map(({ q, label }) => {
                    const locked = isFree && q !== 'low';
                    const cost = QUALITY_MULTIPLIER[q] * frames.length;
                    return (
                      <button
                        key={q}
                        type="button"
                        disabled={locked}
                        onClick={() => onRegenerateWithQuality(q)}
                        title={locked ? 'Upgrade to unlock this quality' : undefined}
                        className={`
                          w-full flex items-center justify-between px-3.5 py-2.5 rounded-xl border text-sm font-bold transition-all
                          ${locked
                            ? 'border-surface-dark/20 bg-surface/50 text-dark-muted/60 cursor-not-allowed'
                            : 'border-surface-dark/40 bg-white hover:border-primary hover:text-primary text-dark cursor-pointer'}
                        `}
                      >
                        <span className="inline-flex items-center gap-1.5">
                          {locked && <i className="ri-lock-2-line" />}
                          {label}
                        </span>
                        <span className="text-xs font-bold text-dark-muted">
                          {cost} cr
                        </span>
                      </button>
                    );
                  })}
                </div>
              </FashionFooterPill>
            )
          )}
          {/* Edit Text (Jul 14) — lift baked-in text into editable overlays.
              Same action row as the other editor buttons. Hidden once the post
              is already editable (parent omits onEditText). Disabled mid-render. */}
          {onEditText && (
            <button
              type="button"
              onClick={onEditText}
              disabled={editingText || editTextDisabled || mutating}
              title={editTextTitle ?? 'Lift the baked-in text into editable layers'}
              className={actionPillClass}
            >
              {editingText
                ? <><i className="ri-loader-4-line animate-spin text-base" />Removing text…</>
                : <>
                    <i className="ri-text text-base" />
                    {frames.length > 1
                      ? `Edit Text · Slide ${activeFrameIdx + 1}`
                      : `Edit Text (${editTextCredits} cr)`}
                  </>}
            </button>
          )}
          {/* Marquee edit (Jun 24) — toggle. Active state flips the preview
              into "drag a rect" mode; the MarqueeOverlay renders inside the
              preview. Disabled mid-render. */}
          {marqueeEnabled && (
            <button
              type="button"
              onClick={() => setMarqueeMode((v) => !v)}
              disabled={mutating}
              title="Edit a region of the image"
              className={
                marqueeMode
                  ? 'h-11 px-4 inline-flex items-center justify-center gap-1.5 rounded-full bg-primary text-white border border-primary text-xs font-bold uppercase tracking-[0.08em] cursor-pointer shadow-sm shadow-primary/20 disabled:opacity-50'
                  : actionPillClass
              }
            >
              <i className="ri-crop-line text-base" />
              {marqueeMode ? 'Marquee on' : 'Marquee edit'}
            </button>
          )}
          {/* Post — outlined pill (renamed from Schedule). Disabled while
              a re-render is in flight (state about to change). */}
          <button
            type="button"
            onClick={onSchedule}
            disabled={saveState === 'saving' || mutating}
            className={actionPillClass}
          >
            <i className="ri-send-plane-line text-base" />
            Post
          </button>
          {/* Download — text + icon. Disabled mid-render. */}
          <button
            type="button"
            onClick={onDownload}
            disabled={downloading || renderBusy}
            className={actionPillClass}
          >
            {downloading
              ? <i className="ri-loader-4-line animate-spin text-base" />
              : <i className="ri-download-2-line text-base" />}
            Download
          </button>
          {/* Save edits — filled primary CTA, RIGHTMOST. Disabled mid-
              render (the base is about to change → saving now would
              persist stale bytes). */}
          <button
            type="button"
            onClick={onManualSave}
            disabled={saveState === 'saving' || mutating}
            title="Save your edits to My Creatives"
            className={`
              h-11 px-5 inline-flex items-center justify-center gap-1.5 rounded-full
              text-xs font-bold uppercase tracking-[0.08em] shadow-md transition-all cursor-pointer
              disabled:opacity-50 disabled:cursor-not-allowed
              ${creativeDirty
                ? 'bg-primary hover:bg-primary-hover text-white shadow-primary/25'
                : 'bg-white border border-surface-dark/40 hover:border-primary hover:text-primary text-dark'}
            `}
          >
            {saveState === 'saving'
              ? <i className="ri-loader-4-line animate-spin text-base" />
              : <i className="ri-save-3-line text-base" />}
            {creativeDirty ? 'Save edits' : 'Save'}
          </button>
        </div>
      </div>

      {/* ============ Image stage (now on the LEFT via order) ============ */}
      <div className="order-1 xl:order-1 min-w-0 h-full min-h-[440px]">
        {/* Single container (white-halo chrome) holding BOTH the image stage
            and the thumbnail strip, so the thumbs read as part of the panel
            (point 1). */}
        <div className="h-full rounded-2xl overflow-hidden shadow-[0_0_0_4px_#ffffff,0_25px_50px_-12px_rgba(15,15,30,0.18)] bg-gradient-to-br from-surface to-surface-dark/40 flex flex-col">
          {/* Image stage — fills remaining height; the image letterboxes here
              (point 5). Measured (stageRef) so the image box fits inside it. */}
          <div ref={stageRef} className="flex-1 min-h-0 flex items-center justify-center p-3">
        <div
          ref={previewRef}
          className={`relative rounded-xl overflow-hidden bg-surface ${aspectClass(aspectRatio)}`}
          style={{ width: `${boxW}px` }}
        >
          {/* Save status — top-right overlay. Dirty-aware: clean = emerald
              "Saved"; unsaved overlay/prompt edits = amber "Unsaved". */}
          {saveState === 'saving' ? (
            <div className="absolute top-3 right-3 z-30">
              <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/90 backdrop-blur-sm shadow-md text-xs font-bold text-dark-muted pointer-events-none">
                <i className="ri-loader-4-line animate-spin" /> Saving…
              </span>
            </div>
          ) : saveState === 'error' ? (
            <div className="absolute top-3 right-3 z-30">
              <button
                type="button"
                onClick={onRetrySave}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/90 backdrop-blur-sm shadow-md text-xs font-bold text-red-600 cursor-pointer"
              >
                <i className="ri-error-warning-line" /> Save failed — retry
              </button>
            </div>
          ) : saveState === 'saved' ? (
            <div className="absolute top-3 right-3 z-30">
              {creativeDirty ? (
                <span
                  title="You have edits that aren't saved yet — hit Save"
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/90 backdrop-blur-sm shadow-md text-xs font-bold text-amber-600 pointer-events-none"
                >
                  <i className="ri-edit-circle-line" /> Unsaved edits
                </span>
              ) : (
                <span
                  title="Saved to My Creatives"
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/90 backdrop-blur-sm shadow-md text-xs font-bold text-emerald-600 pointer-events-none"
                >
                  <i className="ri-check-line" /> Saved
                </span>
              )}
            </div>
          ) : null}

          {/* In-image Save removed Jun 17 — Save edits is now the rightmost
              action-row button (right panel). The Saved/Unsaved status pill
              above stays as the ambient indicator. */}

          {activeImage ? (
            <img
              src={activeImage}
              alt="Generated ad"
              className="w-full h-full object-contain"
              draggable={false}
              onMouseDown={() => setActiveId(null)}
            />
          ) : null}

          {/* Render veil — takes over the image while ANY image-transforming
              op runs (re-render / Apply edit / marquee). pointer-events-auto
              also blocks dragging overlays or the marquee beneath it. High z so
              it sits above the save-status pill + overlays. */}
          {renderBusy && (
            <div className="absolute inset-0 z-50 flex flex-col items-center justify-center gap-3 bg-dark/50 backdrop-blur-[2px] pointer-events-auto">
              <div className="w-12 h-12 rounded-full border-[3px] border-white/30 border-t-white animate-spin" />
              <p className="text-white text-sm font-bold tracking-wide drop-shadow" aria-live="polite">
                {renderBusyLabel}
              </p>
              <p className="text-white/80 text-xs px-6 text-center max-w-[240px]">
                This can take ~25s — please don&apos;t navigate away or you&apos;ll lose the credits.
              </p>
            </div>
          )}

          {/* Marquee edit overlay (Jun 24) — when marqueeMode is on, this
              captures all mouse events on the preview so the user can drag
              a rect + type an instruction. Hidden while a preview is pending
              (marqueePreviewActive) — the Keep/Discard bar owns the stage
              until the user decides. */}
          {marqueeEnabled && !marqueePreviewActive && (
            <MarqueeOverlay
              enabled={marqueeMode}
              applying={marqueeApplying}
              previewRef={previewRef}
              onApply={(rect, instruction) => {
                onMarqueeApply?.(rect, instruction);
              }}
              onCancel={() => setMarqueeMode(false)}
              undoStackSize={marqueeHistoryCount}
              onUndo={onMarqueeUndo}
              undoing={marqueeUndoing}
            />
          )}

          {/* Marquee preview → Keep/Discard bar (Jul 17). The displayed frame
              is a rendered-but-unsaved region edit. Keep forks a new version;
              Discard reverts to the pre-edit image. The credit was already
              charged when the render ran — Discard does NOT refund. */}
          {marqueePreviewActive && (
            <div className="absolute inset-x-0 bottom-0 z-[60] pointer-events-auto">
              <div className="m-3 rounded-2xl bg-dark/90 backdrop-blur-sm shadow-[0_20px_40px_-12px_rgba(0,0,0,0.6)] ring-1 ring-white/10 px-4 py-3 flex items-center gap-3">
                <div className="min-w-0 flex-1">
                  <p className="text-white text-sm font-bold leading-tight">
                    Apply these changes?
                  </p>
                  <p className="text-white/70 text-[11px] leading-tight mt-0.5">
                    This is a preview — Keep saves it as a new version, Discard goes back to the original.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={onMarqueeDiscard}
                  disabled={marqueeKeepSaving}
                  className="h-9 px-4 inline-flex items-center gap-1.5 rounded-full bg-white/10 hover:bg-white/20 text-white text-xs font-bold cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                >
                  <i className="ri-arrow-go-back-line" /> Discard
                </button>
                <button
                  type="button"
                  onClick={onMarqueeKeep}
                  disabled={marqueeKeepSaving}
                  className="h-9 px-4 inline-flex items-center gap-1.5 rounded-full bg-primary hover:bg-primary-hover text-white text-xs font-bold shadow-md shadow-primary/30 cursor-pointer disabled:opacity-60 disabled:cursor-wait transition-colors"
                >
                  {marqueeKeepSaving ? (
                    <><i className="ri-loader-4-line animate-spin" /> Saving…</>
                  ) : (
                    <><i className="ri-check-line" /> Keep</>
                  )}
                </button>
              </div>
            </div>
          )}

          {/* Draggable text overlays — render the RESOLVED layer so the
              visible position matches the canvas output exactly. */}
          {resolvedCurrent.map((overlay) => (
            <div
              key={overlay.id}
              onMouseDown={(e) => {
                e.preventDefault();
                const r = (e.currentTarget as HTMLElement).getBoundingClientRect();
                const previewRect = previewRef.current?.getBoundingClientRect();
                dragBoxRef.current = { w: r.width, h: r.height };
                if (previewRect && previewRect.width > 0 && previewRect.height > 0) {
                  const cursorX = (e.clientX - previewRect.left) / previewRect.width;
                  const cursorY = (e.clientY - previewRect.top) / previewRect.height;
                  dragOffsetRef.current = {
                    dx: cursorX - overlay.x,
                    dy: cursorY - overlay.y,
                  };
                }
                if (overlay.stackOnPrevious) {
                  onOverlaysChange(overlaysRef.current.map((o) =>
                    o.id === overlay.id
                      ? { ...o, y: overlay.y, stackOnPrevious: false }
                      : o,
                  ));
                }
                setActiveId(overlay.id);
                setDraggingId(overlay.id);
              }}
              onTouchStart={(e) => {
                const r = (e.currentTarget as HTMLElement).getBoundingClientRect();
                const previewRect = previewRef.current?.getBoundingClientRect();
                const t = e.touches[0];
                dragBoxRef.current = { w: r.width, h: r.height };
                if (t && previewRect && previewRect.width > 0 && previewRect.height > 0) {
                  const cursorX = (t.clientX - previewRect.left) / previewRect.width;
                  const cursorY = (t.clientY - previewRect.top) / previewRect.height;
                  dragOffsetRef.current = {
                    dx: cursorX - overlay.x,
                    dy: cursorY - overlay.y,
                  };
                }
                if (overlay.stackOnPrevious) {
                  onOverlaysChange(overlaysRef.current.map((o) =>
                    o.id === overlay.id
                      ? { ...o, y: overlay.y, stackOnPrevious: false }
                      : o,
                  ));
                }
                setActiveId(overlay.id);
                setDraggingId(overlay.id);
              }}
              className={`absolute select-none ${
                draggingId === overlay.id ? 'cursor-grabbing' : 'cursor-grab'
              } ${
                activeId === overlay.id ? 'ring-2 ring-primary ring-offset-2 ring-offset-transparent' : ''
              }`}
              style={getTextLayerStyle(overlay, { previewFontScale })}
            >
              {overlay.text}
            </div>
          ))}

          {/* Brand-logo overlays — drawn LAST (above text). */}
          {currentLogos.map((logo) => (
            <img
              key={logo.id}
              src={logo.src}
              alt="Brand logo"
              draggable={false}
              onMouseDown={(e) => {
                e.preventDefault();
                const r = (e.currentTarget as HTMLElement).getBoundingClientRect();
                const previewRect = previewRef.current?.getBoundingClientRect();
                dragBoxRef.current = { w: r.width, h: r.height };
                if (previewRect && previewRect.width > 0 && previewRect.height > 0) {
                  const cursorX = (e.clientX - previewRect.left) / previewRect.width;
                  const cursorY = (e.clientY - previewRect.top) / previewRect.height;
                  dragOffsetRef.current = {
                    dx: cursorX - logo.x,
                    dy: cursorY - logo.y,
                  };
                }
                draggingLogoRef.current = true;
                setActiveId(logo.id);
                setDraggingId(logo.id);
              }}
              onTouchStart={(e) => {
                const r = (e.currentTarget as HTMLElement).getBoundingClientRect();
                const previewRect = previewRef.current?.getBoundingClientRect();
                const t = e.touches[0];
                dragBoxRef.current = { w: r.width, h: r.height };
                if (t && previewRect && previewRect.width > 0 && previewRect.height > 0) {
                  const cursorX = (t.clientX - previewRect.left) / previewRect.width;
                  const cursorY = (t.clientY - previewRect.top) / previewRect.height;
                  dragOffsetRef.current = {
                    dx: cursorX - logo.x,
                    dy: cursorY - logo.y,
                  };
                }
                draggingLogoRef.current = true;
                setActiveId(logo.id);
                setDraggingId(logo.id);
              }}
              className={`absolute select-none ${
                draggingId === logo.id ? 'cursor-grabbing' : 'cursor-grab'
              } ${
                activeId === logo.id ? 'ring-2 ring-primary ring-offset-2 ring-offset-transparent' : ''
              }`}
              style={{
                left: `${logo.x * 100}%`,
                top: `${logo.y * 100}%`,
                width: `${logo.widthFrac * 100}%`,
                height: 'auto',
                opacity: logo.opacity,
                filter: logoFilterCss(logo.tint),
                pointerEvents: 'auto',
              }}
              data-logo-id={logo.id}
            />
          ))}
        </div>{/* /image box */}
          </div>{/* /image stage (stageRef) */}

          {/* Frame thumbs — INSIDE the container (point 1), flex-shrink-0 so
              they take from the stage height (never grow past the column).
              A hairline divider separates them from the image stage. */}
          {frames.length > 1 && (
            <div className="flex-shrink-0 flex items-center justify-center gap-2 px-3 pb-3 pt-2 border-t border-white/50">
              {frames.map((img, i) => (
                <button
                  key={i}
                  type="button"
                  onClick={() => { onActiveFrameChange(i); setActiveId(null); }}
                  className={`relative w-14 h-14 rounded-xl overflow-hidden border-2 transition-all cursor-pointer ${
                    activeFrameIdx === i ? 'border-primary shadow-md' : 'border-transparent hover:border-surface-dark/50'
                  }`}
                >
                  <img src={img} alt={`Frame ${i + 1}`} className="w-full h-full object-cover" draggable={false} />
                  <span className={`absolute inset-0 flex items-center justify-center text-xs font-bold ${
                    activeFrameIdx === i ? 'text-primary' : 'text-white drop-shadow-md'
                  }`}>
                    {i + 1}
                  </span>
                  {overlays.some((o) => o.imageIndex === i) && (
                    <span className="absolute bottom-0.5 right-0.5 w-2 h-2 rounded-full bg-accent" />
                  )}
                </button>
              ))}
            </div>
          )}
        </div>{/* /container */}
      </div>{/* /right column */}
    </div>
  );
}


export default StudioFinishEditor;
